import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    isLoading: false,
    error: null,
    totalItems: 0,
    currentPage: 1,
};

const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        // Socket connection status
        setConnectionStatus(state, action) {
            state.isConnected = action.payload;
        },

        // Add new notification (from socket)
        addNotification(state, action) {
            const notification = action.payload;
            // Add to beginning of array (newest first)
            state.notifications.unshift(notification);
            // Increment unread count if not read
            if (!notification.isRead) {
                state.unreadCount += 1;
            }
        },

        // Set all notifications (from API)
        setNotifications(state, action) {
            const { data, totalItems, page } = action.payload;
            state.notifications = data || [];
            state.totalItems = totalItems || 0;
            state.currentPage = page || 1;
            // Calculate unread count
            state.unreadCount = state.notifications.filter(n => !n.isRead).length;
        },

        // Mark single notification as read
        markAsRead(state, action) {
            const id = action.payload;
            const notification = state.notifications.find(n => n._id === id);
            if (notification && !notification.isRead) {
                notification.isRead = true;
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        },

        // Mark all notifications as read
        markAllAsRead(state) {
            state.notifications.forEach(notification => {
                notification.isRead = true;
            });
            state.unreadCount = 0;
        },

        // Delete notification
        deleteNotification(state, action) {
            const id = action.payload;
            const notification = state.notifications.find(n => n._id === id);
            if (notification && !notification.isRead) {
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
            state.notifications = state.notifications.filter(n => n._id !== id);
        },

        // Set loading state
        setLoading(state, action) {
            state.isLoading = action.payload;
        },

        // Set error
        setError(state, action) {
            state.error = action.payload;
        },

        // Clear all notifications
        clearNotifications(state) {
            state.notifications = [];
            state.unreadCount = 0;
            state.totalItems = 0;
            state.currentPage = 1;
        },

        // Update unread count
        setUnreadCount(state, action) {
            state.unreadCount = action.payload;
        },
    },
});

export const {
    setConnectionStatus,
    addNotification,
    setNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    setLoading,
    setError,
    clearNotifications,
    setUnreadCount,
} = notificationSlice.actions;

// Selectors
export const selectNotifications = state => state.notification.notifications;
export const selectUnreadCount = state => state.notification.unreadCount;
export const selectIsConnected = state => state.notification.isConnected;
export const selectIsLoading = state => state.notification.isLoading;
export const selectError = state => state.notification.error;
export const selectTotalItems = state => state.notification.totalItems;
export const selectCurrentPage = state => state.notification.currentPage;

export default notificationSlice.reducer;
